import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  BookOpen, 
  FileSpreadsheet, 
  Scale,
  Download,
  Calendar,
  Filter
} from "lucide-react";

const formatCurrency = (amount, devise = "FCFA") => {
  if (!amount) return "0 FCFA";
  return new Intl.NumberFormat("fr-FR").format(amount) + " " + devise;
};

const OHADA_ACCOUNTS = {
  "411": "Clients",
  "4191": "Clients - Avances reçues",
  "512": "Banques",
  "571": "Caisse",
  "701": "Ventes de produits",
  "706": "Prestations de services",
  "4431": "TVA facturée",
};

export default function Comptabilite() {
  const [activeTab, setActiveTab] = useState("journal");
  const [journal, setJournal] = useState(null);
  const [grandLivre, setGrandLivre] = useState([]);
  const [balance, setBalance] = useState(null);
  const [balanceClients, setBalanceClients] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    journal: "",
    compte: "",
    dateDebut: "",
    dateFin: ""
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === "journal") {
        const params = new URLSearchParams();
        if (filters.journal) params.append("journal", filters.journal);
        if (filters.dateDebut) params.append("date_debut", filters.dateDebut);
        if (filters.dateFin) params.append("date_fin", filters.dateFin);
        const res = await apiClient.get(`/comptabilite/journal?${params}`);
        setJournal(res.data);
      } else if (activeTab === "grandLivre") {
        const params = filters.compte ? `?compte=${filters.compte}` : "";
        const res = await apiClient.get(`/comptabilite/grand-livre${params}`);
        setGrandLivre(res.data);
      } else if (activeTab === "balance") {
        const res = await apiClient.get("/comptabilite/balance");
        setBalance(res.data);
      } else if (activeTab === "balanceClients") {
        const res = await apiClient.get("/comptabilite/balance-clients");
        setBalanceClients(res.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données comptables");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Comptabilité</h1>
          <p className="text-zinc-500 mt-1">Plan comptable OHADA - Loi gabonaise</p>
        </div>
        <Badge className="bg-emerald-900/50 text-emerald-300 border-emerald-800 w-fit">
          SYSCOHADA Révisé
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="journal" className="data-[state=active]:bg-blue-600">
            <BookOpen className="w-4 h-4 mr-2" />
            Journal
          </TabsTrigger>
          <TabsTrigger value="grandLivre" className="data-[state=active]:bg-blue-600">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Grand Livre
          </TabsTrigger>
          <TabsTrigger value="balance" className="data-[state=active]:bg-blue-600">
            <Scale className="w-4 h-4 mr-2" />
            Balance
          </TabsTrigger>
          <TabsTrigger value="balanceClients" className="data-[state=active]:bg-blue-600">
            <Calendar className="w-4 h-4 mr-2" />
            Balance Clients
          </TabsTrigger>
        </TabsList>

        {/* Journal */}
        <TabsContent value="journal" className="mt-6">
          <Card className="bg-[#121214] border-zinc-800 mb-4">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <Select value={filters.journal || "all"} onValueChange={(v) => setFilters({...filters, journal: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800">
                    <SelectValue placeholder="Type de journal" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="VT">Ventes (VT)</SelectItem>
                    <SelectItem value="TR">Trésorerie (TR)</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={filters.dateDebut}
                  onChange={(e) => setFilters({...filters, dateDebut: e.target.value})}
                  className="w-[180px] bg-zinc-900/50 border-zinc-800"
                  placeholder="Date début"
                />
                <Input
                  type="date"
                  value={filters.dateFin}
                  onChange={(e) => setFilters({...filters, dateFin: e.target.value})}
                  className="w-[180px] bg-zinc-900/50 border-zinc-800"
                  placeholder="Date fin"
                />
                <Button onClick={loadData} className="bg-blue-600 hover:bg-blue-700">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtrer
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="font-heading text-white">Journal Comptable</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-zinc-500 text-center py-8">Chargement...</p>
              ) : !journal?.ecritures?.length ? (
                <p className="text-zinc-500 text-center py-8">Aucune écriture comptable</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-3 px-2 text-zinc-400 font-medium">Date</th>
                          <th className="text-left py-3 px-2 text-zinc-400 font-medium">Journal</th>
                          <th className="text-left py-3 px-2 text-zinc-400 font-medium">Compte</th>
                          <th className="text-left py-3 px-2 text-zinc-400 font-medium">Libellé</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Débit</th>
                          <th className="text-right py-3 px-2 text-zinc-400 font-medium">Crédit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {journal.ecritures.map((e, i) => (
                          <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                            <td className="py-2 px-2 font-mono text-zinc-300">{e.date_piece}</td>
                            <td className="py-2 px-2">
                              <Badge variant="outline" className="border-zinc-700 text-zinc-400">{e.journal}</Badge>
                            </td>
                            <td className="py-2 px-2">
                              <span className="font-mono text-blue-400">{e.compte}</span>
                              <span className="text-zinc-500 ml-2 text-xs">{OHADA_ACCOUNTS[e.compte] || e.libelle_compte}</span>
                            </td>
                            <td className="py-2 px-2 text-zinc-300">{e.libelle}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-400">
                              {e.debit > 0 ? formatCurrency(e.debit) : ""}
                            </td>
                            <td className="py-2 px-2 text-right font-mono text-amber-400">
                              {e.credit > 0 ? formatCurrency(e.credit) : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-zinc-900/50 font-bold">
                          <td colSpan={4} className="py-3 px-2 text-zinc-300">TOTAUX</td>
                          <td className="py-3 px-2 text-right font-mono text-emerald-400">{formatCurrency(journal.total_debit)}</td>
                          <td className="py-3 px-2 text-right font-mono text-amber-400">{formatCurrency(journal.total_credit)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Badge className={journal.equilibre ? "bg-emerald-900/50 text-emerald-300" : "bg-red-900/50 text-red-300"}>
                      {journal.equilibre ? "✓ Journal équilibré" : "⚠ Journal déséquilibré"}
                    </Badge>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grand Livre */}
        <TabsContent value="grandLivre" className="mt-6">
          <Card className="bg-[#121214] border-zinc-800 mb-4">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <Select value={filters.compte || "all"} onValueChange={(v) => setFilters({...filters, compte: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-[250px] bg-zinc-900/50 border-zinc-800">
                    <SelectValue placeholder="Filtrer par compte" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all">Tous les comptes</SelectItem>
                    {Object.entries(OHADA_ACCOUNTS).map(([code, label]) => (
                      <SelectItem key={code} value={code}>{code} - {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={loadData} className="bg-blue-600 hover:bg-blue-700">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtrer
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {loading ? (
              <p className="text-zinc-500 text-center py-8">Chargement...</p>
            ) : grandLivre.length === 0 ? (
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="py-8 text-center text-zinc-500">
                  Aucune écriture trouvée
                </CardContent>
              </Card>
            ) : (
              grandLivre.map((compte) => (
                <Card key={compte.compte} className="bg-[#121214] border-zinc-800">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <CardTitle className="font-heading text-white">
                        <span className="font-mono text-blue-400 mr-2">{compte.compte}</span>
                        {compte.libelle || OHADA_ACCOUNTS[compte.compte]}
                      </CardTitle>
                      <Badge className={compte.solde >= 0 ? "bg-emerald-900/50 text-emerald-300" : "bg-red-900/50 text-red-300"}>
                        Solde: {formatCurrency(Math.abs(compte.solde))} {compte.solde >= 0 ? "D" : "C"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left py-2 px-2 text-zinc-500">Date</th>
                          <th className="text-left py-2 px-2 text-zinc-500">Libellé</th>
                          <th className="text-right py-2 px-2 text-zinc-500">Débit</th>
                          <th className="text-right py-2 px-2 text-zinc-500">Crédit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compte.ecritures?.slice(0, 10).map((e, i) => (
                          <tr key={i} className="border-b border-zinc-800/50">
                            <td className="py-2 px-2 font-mono text-zinc-400">{e.date_piece}</td>
                            <td className="py-2 px-2 text-zinc-300">{e.libelle}</td>
                            <td className="py-2 px-2 text-right font-mono text-emerald-400">
                              {e.debit > 0 ? formatCurrency(e.debit) : ""}
                            </td>
                            <td className="py-2 px-2 text-right font-mono text-amber-400">
                              {e.credit > 0 ? formatCurrency(e.credit) : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Balance */}
        <TabsContent value="balance" className="mt-6">
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="font-heading text-white">Balance Générale des Comptes</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-zinc-500 text-center py-8">Chargement...</p>
              ) : !balance?.balance?.length ? (
                <p className="text-zinc-500 text-center py-8">Aucune donnée</p>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-900/50">
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Compte</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Libellé</th>
                        <th className="text-right py-3 px-2 text-zinc-400 font-medium">Débit</th>
                        <th className="text-right py-3 px-2 text-zinc-400 font-medium">Crédit</th>
                        <th className="text-right py-3 px-2 text-zinc-400 font-medium">Solde D</th>
                        <th className="text-right py-3 px-2 text-zinc-400 font-medium">Solde C</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balance.balance.map((b) => (
                        <tr key={b.compte} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                          <td className="py-2 px-2 font-mono text-blue-400">{b.compte}</td>
                          <td className="py-2 px-2 text-zinc-300">{b.libelle || OHADA_ACCOUNTS[b.compte]}</td>
                          <td className="py-2 px-2 text-right font-mono text-zinc-300">{formatCurrency(b.debit)}</td>
                          <td className="py-2 px-2 text-right font-mono text-zinc-300">{formatCurrency(b.credit)}</td>
                          <td className="py-2 px-2 text-right font-mono text-emerald-400">
                            {b.solde_debiteur > 0 ? formatCurrency(b.solde_debiteur) : ""}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-amber-400">
                            {b.solde_crediteur > 0 ? formatCurrency(b.solde_crediteur) : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-900/50 font-bold">
                        <td colSpan={2} className="py-3 px-2 text-zinc-300">TOTAUX</td>
                        <td className="py-3 px-2 text-right font-mono text-zinc-200">{formatCurrency(balance.totaux?.debit)}</td>
                        <td className="py-3 px-2 text-right font-mono text-zinc-200">{formatCurrency(balance.totaux?.credit)}</td>
                        <td className="py-3 px-2 text-right font-mono text-emerald-400">{formatCurrency(balance.totaux?.soldes_debiteurs)}</td>
                        <td className="py-3 px-2 text-right font-mono text-amber-400">{formatCurrency(balance.totaux?.soldes_crediteurs)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Clients */}
        <TabsContent value="balanceClients" className="mt-6">
          {balanceClients && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-500 uppercase">À échoir</p>
                  <p className="text-xl font-mono font-bold text-blue-400">{formatCurrency(balanceClients.resume_tranches?.a_echoir)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-500 uppercase">0-30 jours</p>
                  <p className="text-xl font-mono font-bold text-amber-400">{formatCurrency(balanceClients.resume_tranches?.["0_30_jours"])}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-500 uppercase">31-60 jours</p>
                  <p className="text-xl font-mono font-bold text-orange-400">{formatCurrency(balanceClients.resume_tranches?.["31_60_jours"])}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-500 uppercase">61-90 jours</p>
                  <p className="text-xl font-mono font-bold text-red-400">{formatCurrency(balanceClients.resume_tranches?.["61_90_jours"])}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-zinc-500 uppercase">+90 jours</p>
                  <p className="text-xl font-mono font-bold text-red-600">{formatCurrency(balanceClients.resume_tranches?.plus_90_jours)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="font-heading text-white">Balance Âgée des Clients</CardTitle>
                {balanceClients && (
                  <Badge className="bg-blue-900/50 text-blue-300">
                    Total créances: {formatCurrency(balanceClients.total_creances)}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-zinc-500 text-center py-8">Chargement...</p>
              ) : !balanceClients?.details?.length ? (
                <p className="text-zinc-500 text-center py-8">Aucune créance client</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-2 text-zinc-400">Client</th>
                      <th className="text-left py-3 px-2 text-zinc-400">Mission</th>
                      <th className="text-left py-3 px-2 text-zinc-400">Échéance</th>
                      <th className="text-right py-3 px-2 text-zinc-400">Montant dû</th>
                      <th className="text-right py-3 px-2 text-zinc-400">Versé</th>
                      <th className="text-right py-3 px-2 text-zinc-400">Reste</th>
                      <th className="text-center py-3 px-2 text-zinc-400">Retard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {balanceClients.details.map((d, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                        <td className="py-2 px-2 text-zinc-300">{d.client?.entreprise}</td>
                        <td className="py-2 px-2 text-zinc-400">{d.mission}</td>
                        <td className="py-2 px-2 font-mono text-zinc-400">{d.date_prevue}</td>
                        <td className="py-2 px-2 text-right font-mono text-zinc-300">{formatCurrency(d.montant_du)}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-400">{formatCurrency(d.montant_verse)}</td>
                        <td className="py-2 px-2 text-right font-mono text-amber-400">{formatCurrency(d.reste_a_payer)}</td>
                        <td className="py-2 px-2 text-center">
                          {d.jours_retard > 0 ? (
                            <Badge className={
                              d.jours_retard > 90 ? "bg-red-900/50 text-red-300" :
                              d.jours_retard > 30 ? "bg-orange-900/50 text-orange-300" :
                              "bg-amber-900/50 text-amber-300"
                            }>
                              {d.jours_retard}j
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-900/50 text-blue-300">À échoir</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
